import { useCallback, useEffect, useState } from 'react';
import { t, vakMsg } from '../../../common/i18n';
import { Loading } from '../../Loading/index.js';
import { DELIVERY_MODES, getAllDeliveryMethods } from '../DeviceCheckoutDeliveryDetailsUtils.js';
import { DeviceCheckoutDeliveryMethodSelection } from './DeviceCheckoutDeliveryMethodSelection.js';
import { DeviceCheckoutDeliveryModeSelection } from './DeviceCheckoutDeliveryModeSelection.js';
import type { DeliveryMethod } from '../../../generated/api/deliveryMethod.js';
import type { PickupPoint } from '../../../generated/api/pickupPoint.js';
import type {
  DeliveryMethodDetails,
  DeliveryMode,
  DeviceCheckoutDeliveryDetailsBaseProps,
} from '../DeviceCheckoutDeliveryDetailsUtils.js';

interface DeviceCheckoutDeliveryMethodsSelectionListProps extends DeviceCheckoutDeliveryDetailsBaseProps {
  deliveryMode: DeliveryMode;
  isDirectDelivery: boolean;
  onDeliveryModeChosen: (deliveryMode: DeliveryMode) => void;
  hasVakProduct: boolean;
}

const getAvailableDeliveryModes = (deliveryMethods?: DeliveryMethod[]) => {
  const allDeliveryMethods = getAllDeliveryMethods(deliveryMethods);
  // To get a distinct list of available/visible delivery modes from all available delivery methods
  const availableDeliveryModeTypes = [...new Set(allDeliveryMethods?.map(item => item.deliveryModeType))];
  return DELIVERY_MODES.filter(deliveryMode => availableDeliveryModeTypes.includes(deliveryMode.type));
};

export const DeviceCheckoutDeliveryMethodsSelectionList = ({
  deliveryDetails,
  saveDeliveryDetailsToState,
  footerNote,
  deliveryMode,
  onDeliveryModeChosen,
  listOfDeliveryMethods,
  isDirectDelivery,
  hasVakProduct,
}: DeviceCheckoutDeliveryMethodsSelectionListProps) => {
  const [availableDeliveryMethods, setAvailableDeliveryMethods] = useState<DeliveryMethodDetails[]>();

  useEffect(() => {
    if (listOfDeliveryMethods) {
      const methods = deliveryMode?.getDeliveryMethods(listOfDeliveryMethods);
      if (methods) {
        setAvailableDeliveryMethods(methods);
      }
    }
  }, [deliveryMode, listOfDeliveryMethods, isDirectDelivery]);

  const availableDeliveryModes = getAvailableDeliveryModes(listOfDeliveryMethods);
  const selectedDeliveryMethod =
    deliveryDetails && availableDeliveryMethods?.find(method => method.type === deliveryDetails.deliveryMethodType);

  const handlePickupPointSelected = useCallback(
    (pickupPoint: PickupPoint) => {
      saveDeliveryDetailsToState({
        ...deliveryDetails,
        pickupPoint,
      });
    },
    [saveDeliveryDetailsToState] /* TODO: rules-of-hooks */ // eslint-disable-line react-hooks/exhaustive-deps
  );

  return listOfDeliveryMethods ? (
    <>
      {hasVakProduct ? t.XWA9(vakMsg) : ''}
      <DeviceCheckoutDeliveryModeSelection
        deliveryMode={deliveryMode}
        onDeliveryModeChosen={onDeliveryModeChosen}
        availableDeliveryModes={availableDeliveryModes}
      />
      {availableDeliveryMethods && (
        <DeviceCheckoutDeliveryMethodSelection
          deliveryMethods={availableDeliveryMethods}
          selectedDeliveryMethod={selectedDeliveryMethod}
          deliveryDetails={deliveryDetails}
          saveDeliveryDetailsToState={saveDeliveryDetailsToState}
          handlePickupPointSelected={handlePickupPointSelected}
          footerNote={footerNote}
        />
      )}
    </>
  ) : (
    <Loading />
  );
};
